import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { comparisonSchema } from '../components/JsonLd';

const Platform = ({ name, description, pros, cons, bestFor }) => (
  <div className="sf-platform-card">
    <h3>{name}</h3>
    <p className="sf-text-small sf-text-muted mb-3">{description}</p>
    <div className="row g-3 mb-3">
      <div className="col-md-6">
        <div className="sf-pros-label mb-2">✓ Pros</div>
        <ul className="sf-text-small" style={{ paddingLeft: '1rem', margin: 0 }}>
          {pros.map((p, i) => <li key={i} className="mb-1">{p}</li>)}
        </ul>
      </div>
      <div className="col-md-6">
        <div className="sf-cons-label mb-2">✗ Cons</div>
        <ul className="sf-text-small" style={{ paddingLeft: '1rem', margin: 0 }}>
          {cons.map((c, i) => <li key={i} className="mb-1">{c}</li>)}
        </ul>
      </div>
    </div>
    <div style={{ background: 'var(--green-light)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--green-dark)' }}>BEST FOR: </span>
      <span className="sf-text-small" style={{ color: 'var(--green-dark)' }}>{bestFor}</span>
    </div>
  </div>
);

const TradingPlatforms = () => {
  const platforms = [
    {
      name: 'Robinhood',
      description: 'Commission-free investing platform for stocks, ETFs, options, and crypto with no account minimums.',
      pros: [
        'Commission-free trades on stocks, ETFs, options, and crypto',
        'Clean, beginner-friendly mobile and web interface',
        'No account minimums',
        'Fractional shares — invest with as little as $1',
        'Instant deposit functionality',
      ],
      cons: [
        'Limited research and educational tools',
        'No mutual funds, bonds, or traditional retirement accounts',
        'Basic charting capabilities',
        'Limited customer service options',
        'Payment for order flow business model',
      ],
      bestFor: 'Beginners, mobile-first traders, and investors wanting a simple commission-free experience.',
    },
    {
      name: 'Charles Schwab',
      description: 'Full-service brokerage with a comprehensive range of investment products, research tools, and branch locations.',
      pros: [
        'Commission-free stock, ETF, and options trades',
        'Comprehensive research and educational resources',
        '24/7 customer service with physical branch locations',
        'Full product range: stocks, bonds, mutual funds, ETFs',
        'Robust retirement planning tools',
        'Advanced platform (StreetSmart Edge) for active traders',
      ],
      cons: [
        '$1,000 minimum for robo-advisor service',
        'Higher options contract fees than some competitors',
        'Steeper learning curve for beginners',
        'Mutual fund transaction fees for non-Schwab funds',
      ],
      bestFor: 'Long-term investors, retirement savers, and those who value research and may want in-person support.',
    },
    {
      name: 'Fidelity',
      description: 'Comprehensive brokerage with industry-leading research, excellent trade execution, and strong retirement services.',
      pros: [
        'Commission-free stock, ETF, and options trades',
        'Top-tier research and stock screening tools',
        'Excellent trade execution quality',
        'Fractional share trading',
        'Strong retirement planning and accounts',
        'Physical branch locations',
      ],
      cons: [
        'Active Trader Pro platform less intuitive than some competitors',
        'Mobile app can feel complex for beginners',
        'Mutual fund transaction fees for non-Fidelity funds',
        'Limited cryptocurrency options',
      ],
      bestFor: 'Retirement investors, research-focused traders, and anyone who values strong customer service.',
    },
    {
      name: 'TD Ameritrade',
      description: 'Powerful platform with advanced tools for active traders and a wealth of educational resources.',
      pros: [
        'Commission-free stock, ETF, and options trades',
        'Industry-leading thinkorswim trading platform',
        'Extensive educational resources and paper trading',
        '24/7 customer support',
        'Wide range of investment products',
        'No account minimums',
      ],
      cons: [
        'Higher options contract fees',
        'Thinkorswim has a steep learning curve',
        'No fractional share trading',
        'Being integrated into Charles Schwab (may bring changes)',
      ],
      bestFor: 'Active traders, options traders, and investors who want powerful tools and deep educational content.',
    },
    {
      name: 'Webull',
      description: 'Commission-free platform targeting intermediate traders with advanced charting and extended trading hours.',
      pros: [
        'Commission-free trades on stocks, ETFs, options, and crypto',
        'No account minimums',
        'Extended trading hours (4:00 AM to 8:00 PM ET)',
        'Advanced charting with technical indicators',
        'Free real-time market data',
        'Paper trading to practice without real money',
      ],
      cons: [
        'Limited educational resources',
        'No mutual funds, bonds, or IRAs',
        'Limited customer service',
        'Steeper learning curve than Robinhood',
      ],
      bestFor: 'Intermediate traders who want commission-free trading with more technical analysis tools.',
    },
    {
      name: 'Interactive Brokers',
      description: 'Professional-grade platform with global market access, advanced order types, and competitive pricing for serious traders.',
      pros: [
        'Access to markets in 33+ countries',
        'Commission-free US stock and ETF trades',
        'Lowest margin rates in the industry',
        'Advanced order types and algorithmic trading',
        'Powerful Trader Workstation platform',
        'Fractional share trading',
      ],
      cons: [
        'Complex platform with steep learning curve',
        'Not beginner-friendly',
        'Historically weaker customer service',
        'Inactivity fees may apply in some account types',
      ],
      bestFor: 'Professional traders, active investors, international traders, and anyone seeking the lowest margin rates.',
    },
  ];

  return (
    <>
      <SEO
        title="Best Trading Platforms for Beginners 2026 - Comparison Guide | StckFuture"
        description="Compare the best stock trading platforms including Robinhood, Fidelity, Charles Schwab, TD Ameritrade, and more. Find the right broker for your needs."
        canonical="https://www.stckfuture.com/trading-platforms"
      />
      <JsonLd schema={comparisonSchema} />
      <div className="sf-content-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">

              <div className="sf-page-header">
                <span className="sf-eyebrow">Module 6</span>
                <h1>Trading Platforms</h1>
                <p className="lead">A side-by-side comparison of the most popular brokers so you can choose the right one for your situation and investing style.</p>
              </div>

              {platforms.map((p) => (
                <Platform key={p.name} {...p} />
              ))}

              {/* Choosing Guide */}
              <div className="sf-card mt-2">
                <h3>How to choose</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>If you're a beginner</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1">Prioritise a clean, intuitive interface</li>
                      <li className="mb-1">Look for built-in educational resources</li>
                      <li className="mb-1">Choose platforms with responsive customer support</li>
                      <li className="mb-1">Low or no account minimums</li>
                      <li className="mb-1">Fractional shares so you can start small</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>If you're more experienced</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1">Advanced charting and technical indicators</li>
                      <li className="mb-1">Variety of order types beyond market and limit</li>
                      <li className="mb-1">Strong research and fundamental data tools</li>
                      <li className="mb-1">Fast, reliable trade execution</li>
                      <li className="mb-1">API access for algorithmic or automated trading</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="sf-takeaways mt-3">
                <h3>Key takeaways</h3>
                <ul>
                  <li>The best platform is the one that fits your current knowledge level and trading frequency</li>
                  <li>Commission-free trading is now standard — focus on features, not fees on basic trades</li>
                  <li>Start with a simpler platform and upgrade as your skills grow</li>
                  <li>Paper trading (simulated trades with no real money) is available on several platforms — use it to practice</li>
                  <li>Check whether the platform offers retirement accounts (IRA, Roth IRA) if that's part of your plan</li>
                </ul>
              </div>

              <div className="sf-next-page">
                <Link to="/quiz" className="btn-sf-primary">
                  Test your knowledge →
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TradingPlatforms;